import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Fuel, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Signup() {
  const { signup, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    account_name: "",
    name: "",
    email: "",
    password: "",
    accept_terms: false,
    accept_privacy: false,
    marketing_opt_in: false,
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.accept_terms || !form.accept_privacy) {
      setErr("Please accept the Terms of Use and Privacy Policy.");
      return;
    }
    setLoading(true);
    try {
      await signup(form);
      toast.success(`Welcome to FuelTrack, ${form.name || "driver"}!`);
      navigate("/");
    } catch (e2) {
      const msg = formatApiError(e2);
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grain">
      <div className="max-w-lg mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-[#FF5A1F] flex items-center justify-center">
            <Fuel size={22} strokeWidth={2.5} className="text-black" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display text-lg font-black tracking-tighter">FUELTRACK</span>
            <span className="ft-overline text-[9px]">Command · Analyse · Optimise</span>
          </div>
        </div>
        <p className="ft-overline mb-2">CREATE ACCOUNT</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none mb-3">
          Start your garage.
        </h1>
        <p className="text-[#A3A3A3] mb-8">
          Free forever for personal use. 1 vehicle + up to 5 invited members.
        </p>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="signup-form">
          <Input label="Garage / Account name" value={form.account_name} onChange={set("account_name")} required testid="signup-account" placeholder="Rakesh Garage" />
          <Input label="Your name" value={form.name} onChange={set("name")} required testid="signup-name" placeholder="Rakesh Sharma" />
          <Input label="Email" type="email" value={form.email} onChange={set("email")} required testid="signup-email" placeholder="you@example.com" />
          <Input label="Password" type="password" value={form.password} onChange={set("password")} required testid="signup-password" placeholder="At least 6 characters" minLength={6} />

          <div className="space-y-3 border-t border-[#262626] pt-5">
            <Checkbox
              label={<>I accept the <Link to="/legal/terms" className="text-[#FF5A1F] hover:underline" target="_blank">Terms of Use</Link></>}
              checked={form.accept_terms}
              onChange={set("accept_terms")}
              testid="signup-terms"
            />
            <Checkbox
              label={<>I have read the <Link to="/legal/privacy" className="text-[#FF5A1F] hover:underline" target="_blank">Privacy Policy</Link></>}
              checked={form.accept_privacy}
              onChange={set("accept_privacy")}
              testid="signup-privacy"
            />
            <Checkbox
              label="Send me occasional product updates (optional)"
              checked={form.marketing_opt_in}
              onChange={set("marketing_opt_in")}
              testid="signup-marketing"
            />
          </div>

          {err && (
            <div className="text-[#EF4444] text-sm border border-[#EF4444]/30 bg-[#EF4444]/10 p-3">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#FF5A1F] text-black font-bold px-6 py-3 hover:bg-[#E04F1A] transition-colors duration-200 disabled:opacity-60 inline-flex items-center justify-center gap-2"
            data-testid="signup-submit"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
            Create my account
          </button>

          <p className="text-[#A3A3A3] text-sm">
            Already have an account?{" "}
            <Link to="/login" className="text-[#FF5A1F] hover:underline" data-testid="link-login">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

function Input({ label, testid, ...rest }) {
  return (
    <div>
      <label className="ft-overline block mb-2">{label}</label>
      <input
        {...rest}
        className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F] focus:ring-1 focus:ring-[#FF5A1F] transition-colors"
        data-testid={testid}
      />
    </div>
  );
}

function Checkbox({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 text-sm text-[#D4D4D4] cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="mt-1 h-4 w-4 accent-[#FF5A1F]"
        data-testid={testid}
      />
      <span>{label}</span>
    </label>
  );
}
